import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConstantsService } from '../../fidelidade/ConstantsService';
import { RotaGuardClient } from '../../guards/auth-loja.guard';
import { AutorizacaoService } from '../../services/autorizacao.service';
import { ChoiceGroupVM, LibraryGroupVM, ProductEditorVM } from '../produtos-v2.models';
import { ProdutosV2FacadeService } from '../produtos-v2.facade';

type GlobalAdditionalOptionModeV2 = 'review' | 'activation' | 'batch-price';

@Component({
  selector: 'app-tela-editor-adicional-global-v2',
  templateUrl: './tela-editor-adicional-global-v2.component.html',
  styleUrls: ['./tela-editor-adicional-global-v2.component.scss']
})
export class TelaEditorAdicionalGlobalV2Component implements OnInit, OnDestroy {
  empresa: any;
  editor: ProductEditorVM | null = null;
  libraryItem: LibraryGroupVM | null = null;
  group: ChoiceGroupVM | null = null;
  loading = true;
  errorMessage = '';
  requestedOptionMode: GlobalAdditionalOptionModeV2 = 'review';
  sharedProductNames: string[] = [];

  private empresaSubscription?: Subscription;
  private paramsSubscription?: Subscription;
  private querySubscription?: Subscription;
  private loadSequence = 0;

  constructor(
    private constantsService: ConstantsService,
    private facade: ProdutosV2FacadeService,
    private route: ActivatedRoute,
    private router: Router,
    private autorizacaoService: AutorizacaoService
  ) {}

  ngOnInit(): void {
    this.empresaSubscription = this.constantsService.empresa$.subscribe((empresa) => {
      if (!empresa?.catalogo?.id) return;
      this.empresa = empresa;
      void this.load();
    });

    this.paramsSubscription = this.route.params.subscribe(() => {
      if (this.empresa) void this.load();
    });

    this.querySubscription = this.route.queryParams.subscribe((params) => {
      const mode = params['modoOpcoes'];
      this.requestedOptionMode = mode === 'activation' || mode === 'batch-price' ? mode : 'review';
    });
  }

  ngOnDestroy(): void {
    this.empresaSubscription?.unsubscribe();
    this.paramsSubscription?.unsubscribe();
    this.querySubscription?.unsubscribe();
  }

  get canEditStructure(): boolean {
    return !this.empresa?.id || !this.empresa?.catalogo?.compartilhado;
  }

  get canEditPrices(): boolean {
    return this.canEditStructure
      || (!!this.empresa?.catalogo?.precoPorEmpresa
        && RotaGuardClient.alterarPrecoCardapioCompartilhado(this.autorizacaoService.getUser()));
  }

  get canEditAvailability(): boolean {
    return this.canEditStructure || !!this.empresa?.catalogo?.disponibilidadePorEmpresa;
  }

  get canEditOptionDisplay(): boolean {
    return RotaGuardClient.podeEditarExibicaoOpcoes(this.autorizacaoService.getUser(), this.empresa);
  }

  get usageLabel(): string {
    const count = this.sharedProductNames.length || Number(this.libraryItem?.sharedCount || 0);
    if (count === 0) return 'Sem vínculo';
    if (count === 1) return 'Exclusivo';
    return `Compartilhado · ${count} produtos`;
  }

  get isRequired(): boolean {
    return Number(this.group?.min || 0) > 0;
  }

  async load(): Promise<void> {
    const sequence = ++this.loadSequence;
    this.loading = true;
    this.errorMessage = '';
    this.editor = null;
    this.group = null;

    try {
      const additionalId = this.route.snapshot.params['id'];
      await this.facade.carregueCatalogo(this.empresa);
      const library = await this.facade.carregueBiblioteca(this.empresa.catalogo);
      if (sequence !== this.loadSequence) return;

      this.libraryItem = library.find((item) => String(item.id) === String(additionalId)) || null;
      if (!this.libraryItem) {
        throw new Error('Este adicional não foi encontrado no catálogo.');
      }

      const requestedProductId = Number(this.route.snapshot.queryParams['produto'] || 0);
      const sourceProductId = requestedProductId
        || Number(this.libraryItem.sourceProductId || this.libraryItem.productId || 0);
      if (!sourceProductId) {
        throw new Error('Este adicional ainda não possui um produto de origem para edição.');
      }

      const templates = await this.facade.carregueTemplates(this.empresa.catalogo);
      const editor = await this.facade.carregueProduto(this.empresa, sourceProductId, templates);
      if (sequence !== this.loadSequence) return;

      let group = editor.groups.find((item) => String(item.id) === String(additionalId)) || null;
      if (!group && requestedProductId) {
        const fallbackId = Number(this.libraryItem.sourceProductId || this.libraryItem.productId || 0);
        if (fallbackId && fallbackId !== requestedProductId) {
          const fallbackEditor = await this.facade.carregueProduto(this.empresa, fallbackId, templates);
          group = fallbackEditor.groups.find((item) => String(item.id) === String(additionalId)) || null;
          if (group) this.editor = fallbackEditor;
        }
      }

      if (!group) {
        throw new Error('O produto de origem não contém mais este adicional.');
      }

      this.editor = this.editor || editor;
      this.group = group;
      this.sharedProductNames = await this.facade.carregueNomesProdutosDoAdicional(this.empresa.catalogo, additionalId);
      const usageCount = this.sharedProductNames.length;
      this.group.shared = usageCount > 1;
      this.group.sharedCount = usageCount;
      this.group.required = Number(this.group.min || 0) > 0;
      this.libraryItem.sharedCount = usageCount;
      this.libraryItem.shared = usageCount > 1;
      this.libraryItem.sharedProductNames = [...this.sharedProductNames];
    } catch (error: any) {
      if (sequence === this.loadSequence) {
        this.errorMessage = error?.message || 'Não foi possível abrir este adicional.';
      }
    } finally {
      if (sequence === this.loadSequence) this.loading = false;
    }
  }

  onGroupSaved(group: ChoiceGroupVM): void {
    this.group = group;
    if (this.libraryItem) {
      this.libraryItem.name = group.name;
      this.libraryItem.min = group.min;
      this.libraryItem.max = group.max;
      this.libraryItem.required = Number(group.min || 0) > 0;
      this.libraryItem.optionsCount = group.options.length;
    }
  }

  onGroupDeleted(): void {
    this.goBack();
  }

  goBack(): void {
    const productId = Number(this.route.snapshot.queryParams['voltarProduto'] || 0);
    if (productId) {
      void this.router.navigate(['/admin/produtos-v2'], {
        queryParams: { produtoDrawer: productId, drawerTab: 'adicionais' }
      });
      return;
    }

    void this.router.navigate(['/admin/adicionais-v2']);
  }
}
